import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { useStripeCheckout } from '../hooks/useStripeCheckout';
import { useStripePayment } from '../hooks/useStripePayment';
import { useAuth } from '../contexts/AuthContext';
import { PRICING_PLANS, PricingPlan } from '../config/pricing';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';

const PricingCard: React.FC<{
  plan: PricingPlan;
  isPro?: boolean;
  onPurchase: (plan: PricingPlan) => Promise<void>;
  isLoggedIn: boolean;
  isLoading: boolean;
}> = ({ plan, isPro, onPurchase, isLoggedIn, isLoading }) => (
  <Paper
    elevation={0}
    sx={{
      p: 4,
      height: '100%',
      borderRadius: 2,
      border: '1px solid',
      borderColor: isPro ? 'primary.main' : 'divider',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      ...(isPro && {
        border: '2px solid',
        bgcolor: 'primary.main',
        color: 'white',
      }),
    }}
  >
    <Typography 
      variant="h5" 
      component="h2" 
      sx={{ 
        mb: 2, 
        fontWeight: 600,
        color: isPro ? 'inherit' : 'primary.main'
      }}
    >
      {plan.name}
    </Typography>
    <Typography variant="h4" sx={{ mb: 2 }}>
      ${plan.price}
    </Typography>
    <Typography 
      variant="h6" 
      sx={{ 
        mb: 1,
        color: isPro ? 'inherit' : 'primary.main'
      }}
    >
      {plan.tokens.toLocaleString()} Tokens
    </Typography>
    <Typography 
      sx={{ 
        mb: 3,
        color: isPro ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary'
      }}
    >
      {plan.description}
    </Typography>
    <Box sx={{ flexGrow: 1 }}>
      {plan.features.map((feature, index) => (
        <Typography 
          key={index} 
          variant="body2" 
          sx={{ mb: 2 }}
        >
          ✓ {feature}
        </Typography>
      ))}
    </Box>
    <Button
      onClick={() => onPurchase(plan)}
      variant={isPro ? "contained" : "outlined"}
      fullWidth
      disabled={!isLoggedIn || isLoading}
      sx={{ 
        mt: 3,
        ...(isPro && {
          bgcolor: 'white',
          color: 'primary.main',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.9)',
          }
        })
      }}
    >
      {isLoading ? (
        <CircularProgress size={24} color={isPro ? "primary" : "inherit"} />
      ) : isLoggedIn ? (
        'Buy Now'
      ) : (
        'Sign in to Purchase'
      )}
    </Button>
  </Paper>
);

const Pricing: React.FC = () => {
  const { createCheckoutSession, isLoading: checkoutLoading } = useStripeCheckout();
  const { isLoading: directPaymentLoading } = useStripePayment();
  const { user } = useAuth();
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  
  const isLoading = checkoutLoading || directPaymentLoading;

  // Check URL parameters for success/cancel messages
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setSuccessMessage('Payment successful! You can now use your subscription.');
    } else if (params.get('canceled') === 'true') {
      setErrorMessage('Payment canceled. Please try again if you want to subscribe.');
      setErrorOpen(true);
    }
  }, []);

  const handlePurchase = async (plan: PricingPlan) => {
    try {
      setErrorMessage('');
      setSuccessMessage('');
      setErrorOpen(false);
      
      if (!user) {
        navigate('/login');
        return;
      }

      console.log('Starting checkout for plan:', plan.name, 'with price ID:', plan.stripePriceId);
      
      await createCheckoutSession({
        priceId: plan.stripePriceId
      });
    } catch (error: any) {
      console.error('Purchase error:', error);
      const errorMsg = error.message || 'Failed to initiate checkout. Please try again.';
      setErrorMessage(errorMsg);
      setErrorOpen(true);
    }
  };

  const handleCloseError = () => {
    setErrorOpen(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <Navbar />
      <Box sx={{ flex: 1 }}>
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
          {successMessage && (
            <Alert severity="success" sx={{ mb: 4 }}>
              {successMessage}
            </Alert>
          )}
          
          <Typography
            variant="h3"
            component="h1"
            align="center"
            sx={{ mb: 2, fontWeight: 600 }}
          >
            Simple, Transparent Pricing
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            Choose the plan that's right for you
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <PricingCard
                plan={PRICING_PLANS.basic}
                onPurchase={handlePurchase}
                isLoggedIn={!!user}
                isLoading={isLoading}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PricingCard
                plan={PRICING_PLANS.pro}
                isPro
                onPurchase={handlePurchase}
                isLoggedIn={!!user}
                isLoading={isLoading}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PricingCard
                plan={PRICING_PLANS.enterprise}
                onPurchase={handlePurchase}
                isLoggedIn={!!user}
                isLoading={isLoading}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Footer />

      <Snackbar 
        open={errorOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Pricing; 